import { useEffect, useRef, useState } from 'react';
import { CpmBandChip, CpmCheckbox, CpmTypeChip } from '../../../../components/design-system';
import {
    packLabel,
    sourceTypeToRecordType,
} from '../../../../constants/centralProductMaster.constants';
import type { CpmLinkReviewItem } from '../../../../types/centralProductMaster.types';

function storeBrandDisplay(row: CpmLinkReviewItem): string {
    return row.storeBrandName?.trim() || '';
}

function tenantRecordName(row: CpmLinkReviewItem): string {
    return row.name?.trim() || '';
}

function confidenceBandLabel(row: CpmLinkReviewItem): string {
    const top = row.confidenceBand?.trim();
    if (top) return top;
    const fromCandidate = row.matchTrace?.candidates?.[0]?.confidenceBand?.trim();
    return fromCandidate || '';
}

function waitingLabel(iso?: string): string {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days <= 0) {
        const hours = Math.floor(ms / (1000 * 60 * 60));
        if (hours <= 0) return 'just now';
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    if (days === 1) return '1 day';
    return `${days} days`;
}

function confChipClass(conf: number): string {
    if (conf >= 0.8) {
        return 'bg-[var(--cpm-success-bg)] border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]';
    }
    if (conf >= 0.65) {
        return 'bg-[var(--cpm-warning-bg)] border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]';
    }
    return 'bg-[var(--cpm-shortfall-bg)] border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]';
}

interface LinkReviewTableProps {
    items: CpmLinkReviewItem[];
    selectedIds: string[];
    mutating?: boolean;
    onToggle: (row: CpmLinkReviewItem) => void;
    onToggleAll: (checked: boolean) => void;
    onOpen: (row: CpmLinkReviewItem) => void;
    onAccept: (
        row: CpmLinkReviewItem,
        selectedMasterId?: string
    ) => Promise<boolean> | boolean | Promise<void> | void;
}

type RowPhase = 'working' | 'error';

export default function LinkReviewTable({
    items,
    selectedIds,
    mutating,
    onToggle,
    onToggleAll,
    onOpen,
    onAccept,
}: LinkReviewTableProps) {
    const [altMenuId, setAltMenuId] = useState<string | null>(null);
    const [resolved, setResolved] = useState<Record<string, string>>({});
    const [linkPhase, setLinkPhase] = useState<Record<string, RowPhase | undefined>>({});
    const [undoPhase, setUndoPhase] = useState<Record<string, RowPhase | undefined>>({});
    const [pendingMasterById, setPendingMasterById] = useState<Record<string, string | undefined>>(
        {}
    );
    const menuRef = useRef<HTMLDivElement>(null);

    const acceptRow = async (row: CpmLinkReviewItem, selectedMasterId?: string, label?: string) => {
        const id = row._id;
        setAltMenuId(null);
        setLinkPhase((prev) => ({ ...prev, [id]: 'working' }));
        setPendingMasterById((prev) => ({ ...prev, [id]: selectedMasterId }));
        try {
            const result = await onAccept(row, selectedMasterId);
            // Explicit false = fail; true/void = success.
            if (result === false) {
                setLinkPhase((prev) => ({ ...prev, [id]: 'error' }));
                return;
            }
            setLinkPhase((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            setResolved((prev) => ({
                ...prev,
                [id]: label ?? row.matchTrace?.candidates?.[0]?.name ?? 'Linked',
            }));
        } catch {
            setLinkPhase((prev) => ({ ...prev, [id]: 'error' }));
        }
    };

    const undoRow = async (row: CpmLinkReviewItem) => {
        const id = row._id;
        setUndoPhase((prev) => ({ ...prev, [id]: 'working' }));
        // Local undo of optimistic Linked chrome (design). No server revert endpoint yet.
        await new Promise((r) => window.setTimeout(r, 400));
        setUndoPhase((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setResolved((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setPendingMasterById((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const retryAccept = (row: CpmLinkReviewItem) => {
        const masterId = pendingMasterById[row._id];
        const candidates = row.matchTrace?.candidates ?? [];
        const label =
            masterId != null
                ? (candidates.find((c) => c.productVariantId === masterId)?.name ?? 'Linked')
                : (candidates[0]?.name ?? 'Linked');
        void acceptRow(row, masterId, label);
    };

    useEffect(() => {
        if (!altMenuId) return;
        const onDoc = (e: MouseEvent) => {
            if (!menuRef.current?.contains(e.target as Node)) setAltMenuId(null);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [altMenuId]);

    const allSelected = items.length > 0 && items.every((i) => selectedIds.includes(i._id));

    return (
        <div>
            <div
                className="cpm-table-head grid items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)] sticky top-0 z-[14] shadow-[inset_0_-1px_0_var(--cpm-border-default)]"
                style={{
                    gridTemplateColumns: '38px minmax(0,1.3fr) minmax(0,1.1fr) 158px 104px 184px',
                    gap: 14,
                    padding: '12px 18px',
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    color: 'var(--cpm-text-primary)',
                    fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                }}
            >
                <CpmCheckbox
                    checked={allSelected}
                    onChange={(e) => onToggleAll(e.target.checked)}
                />
                <span>Tenant record</span>
                <span>Suggested master candidate</span>
                <span>Confidence &amp; method</span>
                <span>Waiting</span>
                <span className="justify-self-end">Resolve</span>
            </div>
            {items.map((row) => {
                const selected = selectedIds.includes(row._id);
                const candidates = row.matchTrace?.candidates ?? [];
                const top = candidates[0];
                const alts = candidates.slice(1);
                const resolvedLabel = resolved[row._id];
                const accepting = linkPhase[row._id] === 'working';
                const acceptFailed = linkPhase[row._id] === 'error';
                const undoing = undoPhase[row._id] === 'working';
                const undoFailed = undoPhase[row._id] === 'error';
                const altOpen = altMenuId === row._id;
                const pack = packLabel(row);

                return (
                    <div
                        key={row._id}
                        className={[
                            'grid items-center border-t border-[var(--cpm-border-default)] cursor-pointer hover:bg-[var(--cpm-surface-subtle)]',
                            selected ? 'bg-[var(--cpm-purple-tint-3)]' : '',
                        ].join(' ')}
                        style={{
                            gridTemplateColumns:
                                '38px minmax(0,1.3fr) minmax(0,1.1fr) 158px 104px 184px',
                            gap: 14,
                            padding: '15px 18px',
                        }}
                        onClick={() => onOpen(row)}
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <CpmCheckbox checked={selected} onChange={() => onToggle(row)} />
                        </div>
                        <div className="min-w-0 flex flex-col" style={{ gap: 5 }}>
                            <span
                                className="truncate text-[var(--cpm-text-primary)]"
                                style={{
                                    fontSize: 13.5,
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {tenantRecordName(row)}
                            </span>
                            <span
                                className="flex items-center flex-wrap min-w-0 overflow-hidden"
                                style={{ gap: '6px 8px' }}
                            >
                                <CpmTypeChip recordType={sourceTypeToRecordType(row.sourceType)} />
                                {pack ? (
                                    <span
                                        className="min-w-0 truncate text-[var(--cpm-text-secondary)] tabular"
                                        style={{ fontSize: 11.5, fontWeight: 700 }}
                                    >
                                        {pack}
                                    </span>
                                ) : null}
                                <span
                                    className="min-w-0 truncate text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 12, fontWeight: 600 }}
                                >
                                    {storeBrandDisplay(row)}
                                </span>
                            </span>
                        </div>
                        <div className="min-w-0 flex flex-col items-start" style={{ gap: 5 }}>
                            <button
                                type="button"
                                title="Open full review for this candidate"
                                className="max-w-full truncate text-left text-[var(--cpm-text-primary)] hover:text-[var(--cpm-brand-purple)]"
                                style={{ fontSize: 13, fontWeight: 700 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpen(row);
                                }}
                            >
                                {top?.name ?? '—'}
                            </button>
                            <span
                                className="text-[var(--cpm-text-tertiary)] uppercase"
                                style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {row.masterTypeLabel?.trim() || '—'}
                            </span>
                        </div>
                        <div className="flex flex-col items-start" style={{ gap: 5 }}>
                            <CpmBandChip band={confidenceBandLabel(row)} />
                            <span
                                className="text-[var(--cpm-text-secondary)] font-[family-name:var(--cpm-font-mono)]"
                                style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                }}
                            >
                                {row.method?.trim() || ''}
                            </span>
                        </div>
                        <span
                            className="text-[var(--cpm-text-secondary)]"
                            style={{ fontSize: 12.5, fontWeight: 600 }}
                        >
                            {waitingLabel(row.createdAt)}
                        </span>
                        <div
                            className="relative justify-self-end"
                            ref={altOpen ? menuRef : undefined}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {resolvedLabel ? (
                                <div className="flex items-center justify-end" style={{ gap: 8 }}>
                                    <span
                                        className="inline-flex items-center gap-[5px] px-[9px] py-[4px] rounded-full bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)] whitespace-nowrap"
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.03em',
                                        }}
                                    >
                                        <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                        >
                                            <path
                                                d="M20 6 9 17l-5-5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        Linked
                                    </span>
                                    {undoing ? (
                                        <span
                                            className="inline-flex items-center whitespace-nowrap text-[var(--cpm-text-secondary)]"
                                            style={{ gap: 5, fontSize: 11.5, fontWeight: 800 }}
                                        >
                                            <span
                                                className="cpm-spin"
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    borderWidth: 1.8,
                                                }}
                                            />
                                            Undoing…
                                        </span>
                                    ) : undoFailed ? (
                                        <button
                                            type="button"
                                            title="The link was not removed. Nothing changed."
                                            className="inline-flex items-center text-[var(--cpm-shortfall-text)] whitespace-nowrap"
                                            style={{ gap: 5, fontSize: 11.5, fontWeight: 800 }}
                                            onClick={() => void undoRow(row)}
                                        >
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.4"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                                <path d="M3 3v5h5" />
                                            </svg>
                                            Undo failed — retry
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="text-[var(--cpm-brand-purple)] whitespace-nowrap"
                                            style={{ fontSize: 11.5, fontWeight: 800 }}
                                            onClick={() => void undoRow(row)}
                                        >
                                            Undo
                                        </button>
                                    )}
                                </div>
                            ) : accepting ? (
                                <div
                                    className="flex items-center justify-end text-[var(--cpm-text-secondary)]"
                                    style={{ gap: 7 }}
                                >
                                    <span
                                        className="cpm-spin"
                                        style={{ width: 14, height: 14, borderWidth: 2 }}
                                    />
                                    <span
                                        className="whitespace-nowrap"
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.03em',
                                        }}
                                    >
                                        Linking…
                                    </span>
                                </div>
                            ) : acceptFailed ? (
                                <div className="flex items-center justify-end" style={{ gap: 7 }}>
                                    <span
                                        title="The link was not saved. Nothing changed."
                                        className="inline-flex items-center gap-[5px] px-[9px] py-[4px] rounded-full bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)] whitespace-nowrap"
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.03em',
                                        }}
                                    >
                                        <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                            <path d="M12 9v4" />
                                            <path d="M12 17h.01" />
                                        </svg>
                                        Link failed
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => retryAccept(row)}
                                        className="inline-flex items-center bg-white text-[var(--cpm-brand-navy)] border-[1.5px] border-[var(--cpm-border-strong)] hover:border-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-surface-subtle)]"
                                        style={{
                                            gap: 5,
                                            height: 30,
                                            paddingLeft: 11,
                                            paddingRight: 11,
                                            borderRadius: 8,
                                            fontSize: 11.5,
                                            fontWeight: 800,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                            <path d="M3 3v5h5" />
                                        </svg>
                                        Retry
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="flex items-center justify-end"
                                        style={{ gap: 6 }}
                                    >
                                        <button
                                            type="button"
                                            disabled={mutating}
                                            onClick={() =>
                                                void acceptRow(
                                                    row,
                                                    undefined,
                                                    top?.name ?? 'Linked'
                                                )
                                            }
                                            className="inline-flex items-center text-white bg-[var(--cpm-brand-navy)] hover:brightness-110 hover:-translate-y-px transition-all disabled:opacity-50"
                                            style={{
                                                gap: 5,
                                                height: 30,
                                                paddingLeft: 12,
                                                paddingRight: 12,
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 800,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.6"
                                            >
                                                <path
                                                    d="M20 6 9 17l-5-5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            Accept
                                        </button>
                                        <button
                                            type="button"
                                            title="Pick a different candidate"
                                            onClick={() => setAltMenuId(altOpen ? null : row._id)}
                                            className={[
                                                'inline-flex items-center justify-center bg-white text-[var(--cpm-text-secondary)] transition-all',
                                                altOpen
                                                    ? 'border-[var(--cpm-brand-purple)]'
                                                    : 'border-[var(--cpm-border-strong)]',
                                            ].join(' ')}
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 8,
                                                borderWidth: 1.5,
                                                borderStyle: 'solid',
                                            }}
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                            >
                                                <path
                                                    d="m6 9 6 6 6-6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    {altOpen ? (
                                        <div
                                            className="absolute top-full right-0 z-40 bg-white border border-[var(--cpm-border-default)] rounded-[12px] flex flex-col cpm-animate-pop-in text-left cursor-default"
                                            style={{
                                                marginTop: 8,
                                                width: 308,
                                                padding: 6,
                                                gap: 2,
                                                boxShadow:
                                                    '0 1px 2px rgba(14,8,88,.04), 0 16px 38px -8px rgba(14,8,88,.30)',
                                            }}
                                        >
                                            <span
                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                style={{
                                                    padding: '8px 10px 4px',
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.08em',
                                                }}
                                            >
                                                AI suggested
                                            </span>
                                            <button
                                                type="button"
                                                className="flex items-center w-full text-left rounded-[9px] hover:bg-[var(--cpm-surface-subtle)]"
                                                style={{
                                                    gap: 10,
                                                    padding: '9px 10px',
                                                    background: 'rgba(110,100,240,.06)',
                                                }}
                                                onClick={() =>
                                                    void acceptRow(
                                                        row,
                                                        undefined,
                                                        top?.name ?? 'Linked'
                                                    )
                                                }
                                            >
                                                <div
                                                    className="flex-1 min-w-0 flex flex-col"
                                                    style={{ gap: 2 }}
                                                >
                                                    <span
                                                        className="truncate text-[var(--cpm-text-primary)]"
                                                        style={{ fontSize: 12.5, fontWeight: 800 }}
                                                    >
                                                        {top?.name ?? '—'}
                                                    </span>
                                                    <span
                                                        className="text-[var(--cpm-text-secondary)]"
                                                        style={{ fontSize: 10.5, fontWeight: 700 }}
                                                    >
                                                        Top match
                                                    </span>
                                                </div>
                                                <span
                                                    className={[
                                                        'shrink-0 inline-flex items-center px-[8px] py-[3px] rounded-full border tabular',
                                                        confChipClass(row.confidence ?? 0),
                                                    ].join(' ')}
                                                    style={{ fontSize: 11, fontWeight: 800 }}
                                                >
                                                    {(row.confidence ?? 0).toFixed(2)}
                                                </span>
                                            </button>
                                            {alts.length > 0 ? (
                                                <span
                                                    className="uppercase text-[var(--cpm-text-tertiary)]"
                                                    style={{
                                                        padding: '9px 10px 4px',
                                                        fontSize: 10,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.08em',
                                                    }}
                                                >
                                                    Other candidates
                                                </span>
                                            ) : null}
                                            {alts.map((alt) => (
                                                <button
                                                    key={alt.productVariantId}
                                                    type="button"
                                                    className="flex items-center w-full text-left rounded-[9px] hover:bg-[var(--cpm-surface-subtle)]"
                                                    style={{ gap: 10, padding: '9px 10px' }}
                                                    onClick={() =>
                                                        void acceptRow(
                                                            row,
                                                            alt.productVariantId,
                                                            alt.name
                                                        )
                                                    }
                                                >
                                                    <span
                                                        className="flex-1 min-w-0 truncate text-[var(--cpm-text-primary)]"
                                                        style={{ fontSize: 12.5, fontWeight: 700 }}
                                                    >
                                                        {alt.name}
                                                    </span>
                                                    <span
                                                        className={[
                                                            'shrink-0 inline-flex items-center px-[8px] py-[3px] rounded-full border tabular',
                                                            confChipClass(
                                                                alt.compositeConfidence ?? 0
                                                            ),
                                                        ].join(' ')}
                                                        style={{ fontSize: 11, fontWeight: 800 }}
                                                    >
                                                        {(alt.compositeConfidence ?? 0).toFixed(2)}
                                                    </span>
                                                </button>
                                            ))}
                                            <div
                                                className="bg-[var(--cpm-border-default)]"
                                                style={{ height: 1, margin: '5px 6px' }}
                                            />
                                            <button
                                                type="button"
                                                className="flex items-center rounded-[9px] text-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-surface-subtle)] w-full text-left"
                                                style={{
                                                    gap: 7,
                                                    padding: '8px 10px 9px',
                                                    fontSize: 11.5,
                                                    fontWeight: 800,
                                                }}
                                                onClick={() => {
                                                    setAltMenuId(null);
                                                    onOpen(row);
                                                }}
                                            >
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.2"
                                                >
                                                    <path d="M5 12h14" />
                                                    <path d="m12 5 7 7-7 7" />
                                                </svg>
                                                Open full review for signals &amp; near-duplicates
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
